/** Canonical map of MIME type → file extension (no leading dot). */
export const MIME_TO_EXT: Record<string, string> = {
  // Image
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/x-icon": "ico",
  // Video
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
  // Audio
  "audio/mpeg": "mp3",
  "audio/ogg": "ogg",
  "audio/wav": "wav",
  "audio/mp4": "m4a",
  "audio/webm": "webm",
  // Text
  "text/plain": "txt",
  "text/markdown": "md",
  "text/html": "html",
  "text/css": "css",
  "text/csv": "csv",
  "text/javascript": "js",
  "text/typescript": "ts",
  // Application
  "application/pdf": "pdf",
  "application/json": "json",
  "application/xml": "xml",
  "application/zip": "zip",
  "application/octet-stream": "bin",
};

/**
 * Strict inverse of `MIME_TO_EXT`: extension → MIME type.
 * Where multiple MIME types share an extension, last writer wins — acceptable
 * because this map is only used for extension→MIME lookup, not round-tripping.
 */
export const EXT_TO_MIME: Record<string, string> = Object.fromEntries(
  Object.entries(MIME_TO_EXT).map(([mime, ext]) => [ext, mime]),
);

// ─── Resolver types ───────────────────────────────────────────────────────────

/** Maps a file extension (no dot) to its MIME type. Throws on unknown ext. */
export type MimeResolver = (ext: string) => string;

/** Maps a MIME type string to its file extension (no dot). Throws on unknown MIME type. */
export type MimeToExtResolver = (mimeType: string) => string;

// ─── Pure resolver functions ──────────────────────────────────────────────────

/**
 * Returns the file extension (no dot) for the given MIME type.
 * Throws if the MIME type is not in the canonical map.
 */
export function mimeToExt(mimeType: string): string {
  const ext = MIME_TO_EXT[mimeType];
  if (ext === undefined) {
    throw new Error(`mime: unknown MIME type "${mimeType}"`);
  }
  return ext;
}

/**
 * Returns the MIME type for the given file extension (no dot).
 * Throws if the extension is not in the canonical map.
 */
export function extToMime(ext: string): string {
  const mime = EXT_TO_MIME[ext];
  if (mime === undefined) {
    throw new Error(`mime: unknown extension "${ext}"`);
  }
  return mime;
}
